import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

interface FallingParticle {
  x: number;
  y: number;
  speed: number;
  radius: number;
  opacity: number;
  fadeDirection: number;
  drift: number;
  driftSpeed: number;
  driftPhase: number;
}

@Component({
  selector: 'so-background-container-ui',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './so-background-container.component.html',
  styleUrls: ['./so-background-container.component.scss'],
})
export class SoBackgroundContainerUI implements OnDestroy {
  private readonly _ngZone = inject(NgZone);

  @ViewChild('decorativeCanvas') private _canvasRef?: ElementRef<HTMLCanvasElement>;

  private _ctx: CanvasRenderingContext2D | null = null;
  private _resizeObserver: ResizeObserver | null = null;
  private _rafId: number | null = null;
  private _lastFrameTs = 0;
  private _particles: FallingParticle[] = [];
  private _canvasWidth = 0;
  private _canvasHeight = 0;
  private _mouseX: number | null = null;
  private _mouseY: number | null = null;

  showBackground = input<boolean>(true);

  opacity = input<number>(0.4);

  particleDensity = input<number>(35);

  canvasStyles = computed(() => {
    return {
      opacity: this.opacity(),
    };
  });

  private readonly _animationEffect = effect(() => {
    if (!this.showBackground()) {
      this._stopCanvasAnimation();
      return;
    }

    requestAnimationFrame(() => {
      if (!this.showBackground()) return;
      this._ensureCanvasReady();
    });
  });

  handleMouseMove(event: MouseEvent): void {
    if (!this.showBackground()) return;

    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    this._mouseX = event.clientX - rect.left;
    this._mouseY = event.clientY - rect.top;
  }

  handleMouseLeave(): void {
    this._mouseX = null;
    this._mouseY = null;
  }

  ngOnDestroy(): void {
    this._animationEffect.destroy();
    this._stopCanvasAnimation();
  }

  private _ensureCanvasReady(): void {
    const canvasEl = this._canvasRef?.nativeElement;
    if (!canvasEl) return;

    const ctx = canvasEl.getContext('2d');
    if (!ctx) return;

    this._ctx = ctx;

    if (!this._resizeObserver) {
      this._resizeObserver = new ResizeObserver(() => {
        this._resizeCanvas();
      });
      this._resizeObserver.observe(canvasEl);
    }

    this._resizeCanvas();
    this._initParticles();
    this._startCanvasAnimation();
  }

  private _resizeCanvas(): void {
    const canvasEl = this._canvasRef?.nativeElement;
    const ctx = this._ctx;
    if (!canvasEl || !ctx) return;

    const rect = canvasEl.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const w = Math.round(rect.width * dpr);
    const h = Math.round(rect.height * dpr);

    if (canvasEl.width !== w || canvasEl.height !== h) {
      canvasEl.width = w;
      canvasEl.height = h;
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this._canvasWidth = rect.width;
    this._canvasHeight = rect.height;

    if (this._particles.length > 0) {
      this._adjustParticlesForResize();
    }
  }

  private _initParticles(): void {
    this._particles = [];
    const count = this.particleDensity();

    for (let i = 0; i < count; i++) {
      this._particles.push(this._createParticle(true));
    }
  }

  private _createParticle(randomY = false): FallingParticle {
    const w = this._canvasWidth;
    const h = this._canvasHeight;

    return {
      x: Math.random() * w,
      y: randomY ? Math.random() * h : -10,
      speed: 8 + Math.random() * 20,
      radius: 1 + Math.random() * 2.5,
      opacity: 0.1 + Math.random() * 0.5,
      fadeDirection: Math.random() > 0.5 ? 1 : -1,
      drift: (Math.random() - 0.5) * 0.3,
      driftSpeed: 0.5 + Math.random() * 1.5,
      driftPhase: Math.random() * Math.PI * 2,
    };
  }

  private _adjustParticlesForResize(): void {
    const w = this._canvasWidth;
    const h = this._canvasHeight;

    for (const p of this._particles) {
      if (p.x > w) p.x = Math.random() * w;
      if (p.y > h) p.y = Math.random() * h;
    }
  }

  private _startCanvasAnimation(): void {
    if (this._rafId !== null) return;

    this._lastFrameTs = performance.now();

    this._ngZone.runOutsideAngular(() => {
      const tick = (ts: number) => {
        this._rafId = requestAnimationFrame(tick);
        this._renderFrame(ts);
      };
      this._rafId = requestAnimationFrame(tick);
    });
  }

  private _stopCanvasAnimation(): void {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }

    const canvasEl = this._canvasRef?.nativeElement;
    const ctx = this._ctx;
    if (canvasEl && ctx) {
      ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
    }

    this._ctx = null;
    this._particles = [];
  }

  private _detectTheme(canvasEl: HTMLCanvasElement): 'light' | 'dark' {
    const bgColor = getComputedStyle(canvasEl).getPropertyValue('background-color').trim();

    if (bgColor && bgColor !== 'transparent' && bgColor !== 'rgba(0, 0, 0, 0)') {
      const match = bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (match) {
        const r = parseInt(match[1], 10);
        const g = parseInt(match[2], 10);
        const b = parseInt(match[3], 10);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.5 ? 'light' : 'dark';
      }
    }

    const htmlEl = document.documentElement;
    if (htmlEl.classList.contains('dark') || htmlEl.getAttribute('data-theme') === 'dark') {
      return 'dark';
    }

    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }

    return 'light';
  }

  private _renderFrame(ts: number): void {
    const canvasEl = this._canvasRef?.nativeElement;
    const ctx = this._ctx;
    if (!canvasEl || !ctx) return;

    if (this._canvasWidth === 0 || this._canvasHeight === 0) return;

    const dtMs = ts - this._lastFrameTs;
    this._lastFrameTs = ts;
    const dt = Math.min(0.05, Math.max(0.001, dtMs / 1000));

    const theme = this._detectTheme(canvasEl);

    const cssColor = getComputedStyle(canvasEl)
      .getPropertyValue('--so-secondary')
      .trim();
    const baseColor = cssColor || '#a3bd36';

    ctx.clearRect(0, 0, this._canvasWidth, this._canvasHeight);

    const mouseX = this._mouseX;
    const mouseY = this._mouseY;
    const influenceRadius = 80;
    const repelStrength = 25;

    for (const p of this._particles) {
      p.driftPhase += p.driftSpeed * dt;
      let horizontalDrift = Math.sin(p.driftPhase) * p.drift * 20;
      let verticalOffset = 0;

      if (mouseX !== null && mouseY !== null) {
        const dx = p.x - mouseX;
        const dy = p.y - mouseY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < influenceRadius && distance > 0.1) {
          const factor = 1 - distance / influenceRadius;
          const smoothFactor = factor * factor;
          horizontalDrift += (dx / distance) * repelStrength * smoothFactor;
          verticalOffset = (dy / distance) * repelStrength * smoothFactor * dt;
        }
      }

      p.x += horizontalDrift * dt;
      p.y += p.speed * dt + verticalOffset;

      p.opacity += p.fadeDirection * 0.15 * dt;
      if (p.opacity > 0.6) {
        p.opacity = 0.6;
        p.fadeDirection = -1;
      } else if (p.opacity < 0.1) {
        p.opacity = 0.1;
        p.fadeDirection = 1;
      }

      if (p.y > this._canvasHeight + 10) {
        Object.assign(p, this._createParticle(false));
      }

      if (p.x < -10) p.x = this._canvasWidth + 10;
      if (p.x > this._canvasWidth + 10) p.x = -10;

      this._drawParticle(ctx, p, baseColor, theme);
    }
  }

  private _drawParticle(
    ctx: CanvasRenderingContext2D,
    p: FallingParticle,
    baseColor: string,
    theme: 'light' | 'dark'
  ): void {
    const glowIntensity = theme === 'dark' ? 0.4 : 0.2;
    const coreIntensity = theme === 'dark' ? 0.9 : 0.6;
    const blurAmount = theme === 'dark' ? 12 : 8;

    ctx.save();

    ctx.fillStyle = baseColor;
    ctx.shadowColor = baseColor;
    ctx.shadowBlur = blurAmount;
    ctx.globalAlpha = p.opacity * glowIntensity;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.radius * 2.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.shadowBlur = 0;
    ctx.globalAlpha = p.opacity * coreIntensity;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
    ctx.fill();

    if (theme === 'dark') {
      ctx.globalAlpha = p.opacity * 0.8;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius * 0.4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
